import React, { useState } from 'react'
import { useTheme } from '@/hooks/useTheme'
import { useReservationsAdmin, Reserva } from '@/hooks/useReservationsAdmin'
import { formatDateDisplay } from '@/utils/dateHelpers'
import { getStatusColors } from '@/utils/statusColorMap'
import { FiltrosReporte } from '@/services/reportService'

interface ReservationsReportTableProps {
  filtros: FiltrosReporte
}

const ReservationsReportTable: React.FC<ReservationsReportTableProps> = ({ filtros }) => {
  const { colors } = useTheme()
  const [page, setPage] = useState(0)
  const pageSize = 20

  // Convertir FiltrosReporte a ReservasFiltros
  const { data: response, isLoading, error } = useReservationsAdmin({
    fecha_desde: filtros.fecha_inicio,
    fecha_hasta: filtros.fecha_fin,
    limit: pageSize,
    offset: page * pageSize
  })

  const reservas = response?.reservas || []
  const total = response?.total || 0
  const totalPages = Math.ceil(total / pageSize)

  const getEstadoColor = (estado: string) => {
    let statusType: 'confirmed' | 'pending' | 'cancelled' = 'pending'
    switch (estado) {
      case 'confirmada':
        statusType = 'confirmed'
        break
      case 'en_proceso':
        statusType = 'pending'
        break
      case 'cancelada':
        statusType = 'cancelled'
        break
    }
    return getStatusColors(statusType, colors)
  }

  const getPagoColor = (estado: string) => {
    let statusType: 'confirmed' | 'pending' | 'cancelled' = 'pending'
    switch (estado) {
      case 'pagado':
        statusType = 'confirmed'
        break
      case 'pendiente':
        statusType = 'pending'
        break
      case 'reembolsado':
        statusType = 'cancelled'
        break
    }
    return getStatusColors(statusType, colors)
  }

  const formatearEstado = (estado: string) => {
    return estado.charAt(0).toUpperCase() + estado.slice(1).replace('_', ' ')
  }

  const formatDateShort = (dateStr: string) => {
    if (!dateStr) return '-'
    const [year, month, day] = dateStr.split('-')
    return `${day}/${month}/${year}`
  }

  const calcularDuracion = (entrada: string, salida: string) => {
    const start = new Date(entrada)
    const end = new Date(salida)
    return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24))
  }

  const getUnidadLabel = (unidad?: string) => {
    return unidad === 'dia' ? 'Días' : 'Noches'
  }

  const [grouped, setGrouped] = React.useState(false)
  const unidadLabel = reservas.length > 0 ? getUnidadLabel(reservas[0].unidad_reserva) : 'Noches'

  const handleExport = () => {
    if (reservas.length === 0) return

    const csv = [
      ['ID', 'Cliente', 'Espacio', 'Entrada', 'Salida', unidadLabel, 'Monto', 'Estado', 'Pago'].join(','),
      ...reservas.map((r: Reserva) => {
        const duracion = calcularDuracion(r.fecha_entrada, r.fecha_salida)
        return [
          r.id.slice(0, 8),
          r.cliente_nombre || r.huesped_nombre || '-',
          r.espacio_nombre || '-',
          formatDateShort(r.fecha_entrada),
          formatDateShort(r.fecha_salida),
          duracion,
          r.monto_total,
          r.estado,
          r.estado_pago
        ].join(',')
      })
    ].join('\n')

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `reservas-${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <p className="text-red-600">Error al cargar las reservas</p>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-bold text-gray-900">
          Reservas ({total} total)
        </h3>
        <div className="flex items-center gap-3">
          {isLoading && <span className="text-sm text-gray-500">Cargando...</span>}
          <button
            onClick={() => setGrouped(!grouped)}
            className="px-2 py-1 text-2xl hover:bg-gray-100 transition-all rounded"
            title={grouped ? 'Expandir' : 'Contraer'}
          >
            {grouped ? '+' : '−'}
          </button>
          <button
            onClick={handleExport}
            disabled={reservas.length === 0}
            className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            title="Exportar a CSV"
          >
            📥 CSV
          </button>
        </div>
      </div>

      {/* Tabla */}
      {!grouped && (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-4 font-semibold text-gray-900 text-xs">ID</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-900">Cliente</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-900">Espacio</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-900">Entrada</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-900">Salida</th>
              <th className="text-center py-3 px-4 font-semibold text-gray-900">{unidadLabel}</th>
              <th className="text-right py-3 px-4 font-semibold text-gray-900">Monto</th>
              <th className="text-center py-3 px-4 font-semibold text-gray-900">Estado</th>
              <th className="text-center py-3 px-4 font-semibold text-gray-900">Pago</th>
            </tr>
          </thead>
          <tbody>
            {reservas.length === 0 ? (
              <tr>
                <td colSpan={9} className="py-8 text-center text-gray-500">
                  No hay reservas en este período
                </td>
              </tr>
            ) : (
              reservas.map((reserva: Reserva) => {
                const duracion = calcularDuracion(reserva.fecha_entrada, reserva.fecha_salida)
                const estadoColor = getEstadoColor(reserva.estado)
                const pagoColor = getPagoColor(reserva.estado_pago)
                const clienteNombre = reserva.cliente_nombre || reserva.huesped_nombre || '-'

                return (
                  <tr key={reserva.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-gray-900 font-medium font-mono text-xs cursor-help" title={reserva.id}>
                      {reserva.id.slice(0, 8)}
                    </td>
                    <td className="py-3 px-4 text-gray-700">{clienteNombre}</td>
                    <td className="py-3 px-4 text-gray-700">{reserva.espacio_nombre || '-'}</td>
                    <td className="py-3 px-4 text-gray-700">{formatDateShort(reserva.fecha_entrada)}</td>
                    <td className="py-3 px-4 text-gray-700">{formatDateShort(reserva.fecha_salida)}</td>
                    <td className="py-3 px-4 text-center text-gray-900 font-medium">{duracion}</td>
                    <td className="py-3 px-4 text-right text-gray-900 font-medium">
                      ${reserva.monto_total.toLocaleString('es-CL')}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span
                        className="px-2 py-1 rounded text-xs font-semibold"
                        style={{
                          backgroundColor: estadoColor.lightBackground,
                          color: estadoColor.backgroundColor
                        }}
                      >
                        {formatearEstado(reserva.estado)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span
                        className="px-2 py-1 rounded text-xs font-semibold"
                        style={{
                          backgroundColor: pagoColor.lightBackground,
                          color: pagoColor.backgroundColor
                        }}
                      >
                        {formatearEstado(reserva.estado_pago)}
                      </span>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
      )}

      {/* Paginación */}
      {!grouped && totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            Página {page + 1} de {totalPages} ({total} registros)
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              ← Anterior
            </button>
            <button
              onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
              disabled={page === totalPages - 1}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Siguiente →
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default ReservationsReportTable
